import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/App';
import { 
  Phone, 
  Loader2, 
  Shield,
  ArrowRight,
  User,
  Mail,
  CheckCircle,
  X
} from 'lucide-react';

const PortalSignup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl') || '/';
  const prefillPhone = searchParams.get('phone') || '';
  
  const [step, setStep] = useState('details'); // details, otp, success
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: prefillPhone
  });
  const [otp, setOtp] = useState('');
  const [sentOtp, setSentOtp] = useState('');

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim() || formData.phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await api.post('/portal/auth/request-otp', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim()
      });
      
      setSentOtp(response.data.otp); // Store for dev display
      setStep('otp');
      toast.success('OTP sent to your phone');
      
      // For development - show OTP
      if (response.data.otp) {
        console.log('Development OTP:', response.data.otp);
        toast.info(`Development OTP: ${response.data.otp}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/portal/auth/verify-otp', {
        phone: formData.phone.trim(),
        otp: otp
      });
      
      // Save session
      localStorage.setItem('portal_token', response.data.token);
      localStorage.setItem('portal_guest', JSON.stringify(response.data.guest));
      
      setStep('success');
      toast.success('Account created successfully!');
      
      // Redirect after showing success
      setTimeout(() => {
        navigate(returnUrl);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const response = await api.post('/portal/auth/request-otp', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim()
      });
      
      setSentOtp(response.data.otp);
      toast.success('OTP resent to your phone');
      
      if (response.data.otp) {
        toast.info(`Development OTP: ${response.data.otp}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome!</h2>
            <p className="text-gray-600 mb-4">Your account has been created successfully.</p>
            <p className="text-sm text-gray-500">Redirecting you...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md relative">
        {/* Close Button */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-gray-500 hover:text-gray-700" />
        </button>
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            {step === 'details' ? (
              <User className="h-8 w-8 text-orange-600" />
            ) : (
              <Shield className="h-8 w-8 text-orange-600" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {step === 'details' ? 'Create Account' : 'Verify Phone'}
          </CardTitle>
          <CardDescription>
            {step === 'details' 
              ? 'Sign up to book your perfect stay' 
              : `Enter the 6-digit code sent to ${formData.phone}`}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {step === 'details' ? (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    className="pl-10"
                    data-testid="signup-name-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="pl-10"
                    data-testid="signup-email-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    className="pl-10"
                    data-testid="signup-phone-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">OTP will be sent to this number for verification</p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-orange-500 hover:bg-orange-600"
                disabled={loading}
                data-testid="signup-send-otp-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Send OTP
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div>
                <Label htmlFor="otp">Enter OTP</Label>
                <Input
                  id="otp"
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest mt-1"
                  data-testid="signup-otp-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-orange-500 hover:bg-orange-600"
                disabled={loading || otp.length !== 6}
                data-testid="signup-verify-otp-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Verify & Create Account
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
              
              <div className="flex items-center justify-between text-sm">
                <Button 
                  type="button"
                  variant="ghost" 
                  size="sm"
                  onClick={() => { setStep('details'); setOtp(''); }}
                >
                  Edit Details
                </Button>
                <Button 
                  type="button"
                  variant="ghost" 
                  size="sm"
                  onClick={handleResendOTP}
                  disabled={loading}
                >
                  Resend OTP
                </Button>
              </div>
            </form>
          )}
          
          <div className="mt-6 text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link 
              to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
              className="text-orange-600 hover:underline font-medium"
            >
              Log In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalSignup;
